import React, { useContext, useMemo } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  HubOption,
  HUB_MAX_TEXT_DEST_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { TextDestCondition } from 'dorian-shared/types/branch/HubOption';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../../context/NodeEditContext';
import { BranchSelectItem } from '../../shared/types';
import { getBranchName } from '../../shared/hubUtils';
import { useTextDestConditions } from '../../hooks/useTextDestConditions';
import { TextDestConditionCard } from './TextDestConditionCard';
import { isTextDestConditionValid } from './textDestConditionValidation';
import './HubOptionTextDest.scss';

export type TextDestOverridesProps = {
  optionUuid: string;
  defaultTitle: string;
  defaultGotoBranchId: number | null;
  textDestConditions: TextDestCondition[];
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  isContinue: boolean;
  onChange: (changes: Partial<HubOption>) => void;
};

const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

export function TextDestOverrides({
  optionUuid,
  defaultTitle,
  defaultGotoBranchId,
  textDestConditions,
  branches,
  currentBranchId,
  isDisabled,
  isContinue,
  onChange,
}: TextDestOverridesProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const memoryBankSlots = (ctx?.memoryBankSlots as MemoryDTO[] | undefined) ?? [];
  const memoryVariables = useMemo(
    () => memoryBankSlots.filter((s: MemoryDTO) => s.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
  } = useTextDestConditions(textDestConditions, onChange, memoryVariables, branches);

  const sortableItems = useMemo(
    () => textDestConditions.map((c) => c.uuid),
    [textDestConditions],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const defaultDestLabel = getBranchName(defaultGotoBranchId, branches);
  const otherwiseText = `uses Title "${defaultTitle || '...'}"${defaultDestLabel ? ` → ${defaultDestLabel}` : ''}`;

  return (
    <div className="hub-option-text-dest" data-testid="text-dest-overrides">
      <p className="hub-option-text-dest__helper">
        First match overrides Title + Destination. Evaluated top-to-bottom.
      </p>

      <DndContext
        id={`textDest-dnd-${optionUuid}`}
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <div className="list-group mb-1">
            {textDestConditions.map((cond) => (
              <TextDestConditionCard
                key={cond.uuid}
                condition={cond}
                memoryVariables={memoryVariables}
                branches={branches}
                currentBranchId={currentBranchId}
                isDisabled={isDisabled}
                isContinue={isContinue}
                isValid={isTextDestConditionValid(cond)}
                onChange={(updated) => updateCondition(cond.uuid, updated)}
                onRemove={() => removeCondition(cond.uuid)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Otherwise row */}
      <div className="hub-option-text-dest__otherwise">
        <strong className="small mr-2">Otherwise</strong>
        <small className="text-muted">{otherwiseText}</small>
      </div>

      {/* Add button + counter */}
      <div className="mt-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={addCondition}
          disabled={isDisabled || textDestConditions.length >= HUB_MAX_TEXT_DEST_CONDITIONS}
        >
          + Add Override
        </button>
        <small className="text-muted ml-2">
          {textDestConditions.length} / {HUB_MAX_TEXT_DEST_CONDITIONS}
        </small>
      </div>
    </div>
  );
}
